/**
 * WinProjectModal — oferta de criar um PROJETO quando um Deal é marcado ganho.
 *
 * Abre no fluxo do Comercial (Pipelines) ao soltar um negócio numa etapa de
 * ganho. O usuário escolhe: criar projeto de um template, em branco, ou não
 * criar. Ao criar, vincula projects.deal_id (rastreabilidade venda↔entrega) e
 * pré-preenche nome (título do negócio) + company_id (se houver).
 *
 * Reutilizável em qualquer gatilho de "ganho" — recebe o `deal` e callbacks.
 *
 * Visual: mesmo padrão de modal centrado do módulo Financeiro (fc-modal-*)
 * e cartão de template do Pipelines (pm-tpl-*) — nenhum estilo próprio.
 */

import { useEffect, useState } from 'react'
import Icon from '../../components/icons.jsx'
import { pjListTemplates, pjCreateProject, getDealContacts } from '../../db/api.js'
import '../financeiro/contas.css'
import '../comercial/pipelines.css'

export default function WinProjectModal({ deal, onClose, onCreated }) {
  const [templates, setTemplates] = useState([])
  const [templateKey, setTemplateKey] = useState('vazio')
  const [nome, setNome] = useState(deal?.titulo ? `${deal.titulo}` : '')
  const [contactId, setContactId] = useState(null)   // contato principal do deal
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  useEffect(() => { pjListTemplates().then(setTemplates).catch(() => {}) }, [])

  // vincula o cliente = contato principal (ou o 1º) do negócio ganho
  useEffect(() => {
    if (!deal?.id) return
    let alive = true
    getDealContacts(deal.id)
      .then((cs) => {
        if (!alive || !cs?.length) return
        const primary = cs.find((c) => c.is_primary) || cs[0]
        setContactId(primary?.contact_id || null)
      })
      .catch(() => {})
    return () => { alive = false }
  }, [deal?.id])

  const selected = templates.find((t) => t.key === templateKey)

  const create = async () => {
    if (!nome.trim() || saving) return
    setSaving(true); setErr(null)
    try {
      const proj = await pjCreateProject({
        nome: nome.trim(),
        template_key: templateKey,
        deal_id: deal?.id || null,
        company_id: deal?.company_id || null,
        contact_id: contactId || null,
        prioridade: 'media',
      })
      onCreated?.(proj)
    } catch (e) { setErr(e.message); setSaving(false) }
  }

  return (
    <div className="fc-modal-overlay fc-modal-overlay--top" onMouseDown={onClose}>
      <div className="fc-modal" style={{ width: 540 }} onMouseDown={(e) => e.stopPropagation()}>
        <div className="fc-modal-head">
          <div style={{ display: 'flex', gap: 10, alignItems: 'center', flex: 1, minWidth: 0 }}>
            <span style={{
              width: 34, height: 34, borderRadius: '50%', background: '#e7f9ee', border: '1px solid #bdf0d2',
              display: 'inline-flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0,
            }}>
              <Icon n="check" s={16} c="#17a34a" sw={2.4} />
            </span>
            <div style={{ minWidth: 0 }}>
              <div className="fc-drawer-title">Negócio ganho!</div>
              <div style={{ fontSize: 12.5, color: '#667085', marginTop: 2 }}>
                Transforme a venda em um projeto de entrega.
              </div>
            </div>
          </div>
          <button className="fc-drawer-x" onClick={onClose}><Icon n="x" s={16} sw={2} /></button>
        </div>

        <div className="fc-modal-body">
          {err && <div className="fc-error">{err}</div>}

          <div className="fc-field">
            <label className="fc-label">Nome do projeto</label>
            <input className="fc-input" value={nome} autoFocus onChange={(e) => setNome(e.target.value)}
              placeholder="Nome do projeto" />
          </div>

          <div className="fc-field">
            <label className="fc-label">Template</label>
            <div className="pm-tpl-grid">
              {templates.map((t) => {
                const active = t.key === templateKey
                return (
                  <button key={t.key} type="button"
                    className={`pm-tpl-card${active ? ' active' : ''}`}
                    onClick={() => setTemplateKey(t.key)}>
                    <span style={{ minWidth: 0 }}>
                      <span className="pm-tpl-label">{t.label}</span>
                      <span className="pm-tpl-meta">{t.columns.length} colunas · {t.task_count} tarefas</span>
                    </span>
                  </button>
                )
              })}
            </div>
            {selected && (
              <div className="pm-tpl-chips">
                {selected.columns.map((c, i) => <span key={i} className="pm-tpl-chip">{c}</span>)}
              </div>
            )}
          </div>
        </div>

        <div className="fc-modal-foot">
          <button className="fc-ctrl" onClick={onClose}>Não criar agora</button>
          <button className="fc-ctrl primary" onClick={create} disabled={saving || !nome.trim()}>
            {saving ? 'Criando…' : 'Criar projeto'}
          </button>
        </div>
      </div>
    </div>
  )
}
